import React, { useState } from 'react';
import {
  Box,
  Chip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { techCategories, techStacks, TechStack, TechCategory } from '../utils/techStacks';
import { useLanguage } from '../context/LanguageContext';

interface TechStackSelectorProps {
  selectedStacks: string[];
  onChange: (stacks: string[]) => void;
}

const TechStackSelector: React.FC<TechStackSelectorProps> = ({ selectedStacks, onChange }) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<TechCategory | null>('language');

  // Filtrer les stacks en fonction du terme de recherche
  const filteredStacks = searchTerm
    ? techStacks.filter(stack => 
        stack.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stack.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : techStacks;

  // Gérer la sélection/désélection d'une stack
  const handleToggleStack = (stackId: string) => {
    if (selectedStacks.includes(stackId)) {
      onChange(selectedStacks.filter(id => id !== stackId));
    } else {
      onChange([...selectedStacks, stackId]);
    }
  };

  // Gérer l'expansion des catégories
  const handleExpandCategory = (category: TechCategory) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  // Regrouper les stacks par catégorie
  const stacksByCategory: Record<TechCategory, TechStack[]> = Object.keys(techCategories).reduce((acc, cat) => {
    const category = cat as TechCategory;
    acc[category] = filteredStacks.filter(stack => stack.category === category);
    return acc;
  }, {} as Record<TechCategory, TechStack[]>);

  return (
    <Box>
      <TextField
        fullWidth
        variant="outlined"
        placeholder={t('admin.applications.form.techStack.search') as string}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        margin="normal"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Afficher les stacks sélectionnées */}
      {selectedStacks.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('admin.applications.form.techStack.selected') as string}:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedStacks.map(stackId => {
              const stack = techStacks.find(s => s.id === stackId);
              if (!stack) return null;
              
              return (
                <Chip
                  key={stack.id}
                  label={stack.name}
                  onDelete={() => handleToggleStack(stack.id)}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    bgcolor: `${stack.color}22`, // Couleur avec opacité
                    borderColor: stack.color,
                    '& .MuiChip-label': {
                      color: 'text.primary'
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Afficher les stacks par catégorie */}
      {Object.entries(techCategories).map(([category, label]) => {
        const categoryStacks = stacksByCategory[category as TechCategory];
        
        // Ne pas afficher les catégories vides lors d'une recherche
        if (searchTerm && categoryStacks.length === 0) {
          return null;
        }
        
        return (
          <Accordion 
            key={category}
            expanded={expandedCategory === category || !!searchTerm}
            onChange={() => handleExpandCategory(category as TechCategory)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{label} ({categoryStacks.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {categoryStacks.map(stack => (
                  <Chip
                    key={stack.id}
                    label={stack.name}
                    onClick={() => handleToggleStack(stack.id)}
                    color={selectedStacks.includes(stack.id) ? 'primary' : 'default'}
                    variant={selectedStacks.includes(stack.id) ? 'filled' : 'outlined'}
                    sx={{ 
                      ...(selectedStacks.includes(stack.id) ? {
                        bgcolor: stack.color,
                        color: '#fff',
                        '&:hover': {
                          bgcolor: stack.color
                        }
                      } : {
                        borderColor: stack.color,
                        '&:hover': {
                          bgcolor: `${stack.color}22`
                        }
                      })
                    }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default TechStackSelector; 